import { DatePipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  EventTypes,
  EventUpdateStatus,
  InstallationFeaturesStore,
  NotificationEvent,
  supportedFeatureCommands,
  supportedFeatureNames,
  ViEvent,
} from '@store';
import {
  DeviceNameCommands,
  DeviceNameProperties,
  DTCDeviceNames,
  FeatureCommands,
  FeatureNames,
} from '@vi-iot/client';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_EVENT_LOG } from 'src/app/shared/services/app-insights/events/event-log.events';
import { TemperatureUnitPipe } from '../../features/temperature/pipes';
import { CustomTranslationsService } from '../../operational-data-common/translations';
import { DatesService } from '../../shared/services/dates.service';
import { SerialSpacingPipe } from '@pipes';

('src/app/operational-data-common/label-pipes/id-prefix-remover.pipe');

@Injectable({
  providedIn: 'root',
})
export class EventDescriptionService {
  FEATURES: string[] = supportedFeatureNames;

  private eventDescriptionFactory = (event: NotificationEvent, timeZone: string): string => {
    switch (event.eventType) {
      case EventTypes.DEVICE_BMU_SERIAL_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DEVICE_BMU_SERIAL_CHANGED.DESCRIPTION'
        )}: ${event.body.bmuSerial}`;

      case EventTypes.DEVICE_BOILER_SERIAL_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DEVICE_BOILER_SERIAL_CHANGED.DESCRIPTION'
        )} ${event.body.boilerSerial}`;

      case EventTypes.FIRMWARE_UPDATE_STATUS:
        if (event.body.updateStatus === EventUpdateStatus.FAILED) {
          return `${this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.FIRMWARE_UPDATE_STATUS.FAILED'
          )}`;
        }

        if (event.body.updateStatus === EventUpdateStatus.COMPLETED) {
          const firmwartVerionString = event.body.firmwareVersion
            ? `${event.body.firmwareVersion.major}.${event.body.firmwareVersion.minor}.${event.body.firmwareVersion.build}.${event.body.firmwareVersion.revision}`
            : '';

          return `${this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.FIRMWARE_UPDATE_STATUS.SUCCESSFUL'
          )}
          ${this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.FIRMWARE_UPDATE_STATUS.FIRMWARE_VERSION',
            {
              version: firmwartVerionString,
            }
          )}`;
        }

      case EventTypes.GATEWAY_ONLINE:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.GATEWAY_ONLINE.DESCRIPTION'
        )} ${
          event.body.online
            ? `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ON')}`
            : `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.OFF')}`
        }`;
      case EventTypes.EXTERNAL_LOCK:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EXTERNAL_LOCK.DESCRIPTION'
        )} ${
          event.body.active
            ? `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ON')}`
            : `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.OFF')}`
        }`;
      case EventTypes.EXTERNAL_DEMAND:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EXTERNAL_DEMAND.DESCRIPTION'
        )} ${
          event.body.active
            ? `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ON')}`
            : `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.OFF')}`
        }`;
      case EventTypes.GATEWAY_RESET_COMMAND:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.GATEWAY_RESET_COMMAND.DESCRIPTION'
        )}`;

      case EventTypes.INSTALLATION_ADDRESS_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.INSTALLATION_ADDRESS_CHANGED.DESCRIPTION'
        )} ${event.body.caller}`;

      case EventTypes.FEATURE_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.DESCRIPTION'
        )}`;

      case EventTypes.INVITATION_ACCEPTED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.INVITATION_STATUS.DESCRIPTION'
        )}: ${event.body.inviter}
                        ${this.translateService.instant(
                          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.INVITATION_TO'
                        )}
                        ${event.body.invitee} ${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.INVITATION_HAS_BEEN'
        )} ${`${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ACCEPTED'
        )}`}`;
      case EventTypes.INVITATION_TERMINATED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.INVITATION_STATUS.DESCRIPTION'
        )}: ${event.body.inviter}
                            ${this.translateService.instant(
                              '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.INVITATION_TO'
                            )}
                            ${event.body.invitee} ${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.INVITATION_HAS_BEEN'
        )}
        ${`${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.TERMINATED'
        )}`}`;

      case EventTypes.SRC_STATUS_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SRC_STATUS_CHANGED.DESCRIPTION'
        )} ${
          event.body.active
            ? `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ON')}`
            : `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.OFF')}`
        }`;

      case EventTypes.SNAPSHOT_RESTORE_STATUS_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SNAPSHOT_RESTORE_STATUS_CHANGED.DESCRIPTION'
        )}`;

      case EventTypes.GEOFENCING_ACTIVATION_CHANGED:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.GEOFENCING_ACTIVATION_CHANGED.DESCRIPTION'
        )} ${
          event.body.active
            ? `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.ON')}`
            : `${this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.OFF')}`
        }`;

      case EventTypes.HEATING_ROD_RUN_TOO_LONG:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.HEATING_ROD_RUN_TOO_LONG'
        );

      case EventTypes.VIFLEX:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.VIFLEX_DESCRIPTION',
          {
            startTime: this.datePipe.transform(
              this.datesService.getMomentFormattedDateForSpecificTimezone(
                event.body.startTime,
                timeZone
              ),
              'short'
            ),
            endTime: this.datePipe.transform(
              this.datesService.getMomentFormattedDateForSpecificTimezone(
                event.body.endTime,
                timeZone
              ),
              'short'
            ),
          }
        );

      case EventTypes.DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS_DESCRIPTION',
          {
            savingPotentialAbsolute: event.body.savingPotentialAbsolute,
            savingPotentialRelative: event.body.savingPotentialRelative,
          }
        );

      case EventTypes.SETBACK_DURATION_CAN_BE_INCREASED_BY_ONE_HOUR:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.SETBACK_DURATION_CAN_BE_INCREASED_BY_ONE_HOUR_DESCRIPTION',
          {
            savingPotentialAbsolute: event.body.savingPotentialAbsolute,
            savingPotentialRelative: event.body.savingPotentialRelative,
          }
        );

      case EventTypes.DHW_TARGET_TEMPERATURE_CAN_BE_DECREASED:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.DHW_TARGET_TEMPERATURE_CAN_BE_DECREASED_DESCRIPTION_NEW',
          {
            temperatureCurrent: this.temperatureUnitPipe.transform(event.body.temperatureCurrent),
            temperatureRecommended: this.temperatureUnitPipe.transform(
              event.body.temperatureRecommended
            ),
            savingPotentialAbsolute: event.body.savingPotentialAbsolute,
            savingPotentialRelative: event.body.savingPotentialRelative,
          }
        );

      case EventTypes.DHW_DURATION_CAN_BE_DECREASED:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.DHW_DURATION_CAN_BE_DECREASED_DESCRIPTION',
          {
            durationCurrent: this.datesService.getMomentFormattedDateForTotalMinutes(
              event.body.durationCurrent
            ),
            durationRecommended: this.datesService.getMomentFormattedDateForTotalMinutes(
              event.body.durationRecommended
            ),
            savingPotentialAbsolute: event.body.savingPotentialAbsolute,
            savingPotentialRelative: event.body.savingPotentialRelative,
          }
        );
    }
  };

  private featureChangedDescriptionFactory = (event: NotificationEvent): string => {
    const circuitNumber = (+event.body?.featureName?.match(/[0-9]+/)?.[0] + 1).toString();

    switch (event.body.commandName) {
      case FeatureCommands.setMode:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.SET_MODE)
          .replace(
            /{{value}}/gi,
            this.translateFeatureMode(
              event.body.featureName?.replace(/[0-9]/g, ''),
              event.body.commandBody?.mode
            )
          )
          .replace(/{{X}}/gi, circuitNumber);

      case FeatureCommands.setTargetTemperature:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.SET_TARGET_TEMPERATURE
        )
          .replace(
            /{{value}}/gi,
            this.temperatureUnitPipe.transform(event.body.commandBody.temperature)
          )
          .replace(/{{X}}/gi, circuitNumber);

      case FeatureCommands.setTemperature:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.SET_TEMPERATURE
        )
          .replace(
            /{{value}}/gi,
            this.temperatureUnitPipe.transform(event.body.commandBody.targetTemperature)
          )
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.setCurve:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.SET_CURVE'
        )}`;

      case FeatureCommands.setHysteresis:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.SET_HYSTERESIS'
        )} ${this.temperatureUnitPipe.transform(event.body.commandBody.hysteresis)}`;

      case FeatureCommands.setSchedule:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.SET_SCHEDULE
        )
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.setType:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.SET_TYPE)
          .replace(/{{value}}/gi, event.body.commandBody.type)
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.setValue:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.SET_VALUE)
          .replace(/{{value}}/gi, event.body.commandBody.value)
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.activate:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.ACTIVATE)
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.deactivate:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.DEACTIVATE
        )
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.add:
        return `${this.translateService
          .instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.ADD')
          .replace(/{{roomName}}/gi, event.body.commandBody.name)}`;

      case FeatureCommands.remove:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.REMOVE)
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{name}}/gi, '');

      case FeatureCommands.addDevice:
        return `${this.translateService
          .instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.ADD_DEVICE')
          .replace(/{{deviceId}}/gi, event.body.commandBody.id)}`;

      case FeatureCommands.removeDevice:
        return `${this.translateService
          .instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.REMOVE_DEVICE')
          .replace(/{{deviceId}}/gi, event.body.commandBody.id)}`;

      case FeatureCommands.addActor:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.ADD_ACTOR2',
          {
            deviceId: this.getZigbeeDeviceId(event.body.commandBody.actorDeviceId),
            deviceName: this.getZigbeeDeviceName(event.body.commandBody.actorDeviceId)
              ? this.getZigbeeDeviceName(event.body.commandBody.actorDeviceId)
              : '-',
            roomName: this.getZigbeeDeviceRoomName(event.body.commandBody.actorDeviceId)
              ? this.getZigbeeDeviceRoomName(event.body.commandBody.actorDeviceId)
              : '',
          }
        );

      case FeatureCommands.removeActor:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.REMOVE_ACTOR2',
          {
            deviceId: this.getZigbeeDeviceId(event.body.commandBody.actorDeviceId),
            deviceName: this.getZigbeeDeviceName(event.body.commandBody.actorDeviceId)
              ? this.getZigbeeDeviceName(event.body.commandBody.actorDeviceId)
              : '-',
            roomName: this.getZigbeeDeviceRoomName(event.body.commandBody.actorDeviceId)
              ? this.getZigbeeDeviceRoomName(event.body.commandBody.actorDeviceId)
              : '',
          }
        );

      case FeatureCommands.assignHeatingCircuit:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.ASSIGN_HEATING_CIRCUIT'
        )}`;

      case FeatureCommands.reboot:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.REBOOT'
        )}`;

      case FeatureCommands.clearCacheAndReboot:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.CLEAR_CACHE_AND_REBOOT'
        );

      case FeatureCommands.setTimezone:
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.SET_TIMEZONE',
          {
            timezone: event.body.commandBody.value,
          }
        );

      case FeatureCommands.schedule:
        return this.translateFeatureName(event.body.featureName, supportedFeatureCommands.SCHEDULE)
          .replace(/{{X}}/gi, circuitNumber)
          .replace(/{{startDate}}/gi, event.body.commandBody.start)
          .replace(/{{endDate}}/gi, event.body.commandBody.end);

      case FeatureCommands.unschedule:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.UNSCHEDULE
        ).replace(/{{X}}/gi, circuitNumber);

      case FeatureCommands.setMax:
        return `${this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED.SET_MAX'
        )}`;
      case FeatureCommands.update:
        return this.translateFeatureName(
          event.body.featureName,
          supportedFeatureCommands.UPDATE
        ).replace(/{{version}}/gi, event.body.commandBody.version);
    }
  };

  constructor(
    private datesService: DatesService,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private temperatureUnitPipe: TemperatureUnitPipe,
    private appInsights: AppInsightsMonitoringService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private customTranslationsService: CustomTranslationsService,
    @Inject(SerialSpacingPipe) private serialSpacingPipe: SerialSpacingPipe
  ) {}

  getDescriptionByEventType(event: NotificationEvent, timeZone: string): string {
    if (event.eventType === EventTypes.FEATURE_CHANGED) {
      return this.featureChangedDescriptionFactory(event);
    } else {
      return this.eventDescriptionFactory(event, timeZone);
    }
  }

  getDeviceName(event: ViEvent): string {
    const deviceId = event.body?.deviceId;

    const numberPattern = /^\d+$/;

    if (!deviceId) {
      return;
    }

    if (deviceId.toLocaleLowerCase() === DTCDeviceNames.hems) {
      return this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.HEMS');
    }

    if (deviceId.includes('zigbee-')) {
      return this.getZigbeeDeviceId(deviceId);
    }

    if (numberPattern.test(deviceId)) {
      return this.serialSpacingPipe.transform(deviceId).toString();
    } else {
      return deviceId;
    }
  }

  getZigbeeDeviceId(deviceId: string): string {
    const idPrefixRemoved = deviceId.replace('zigbee-', '').toLowerCase();

    return this.serialSpacingPipe.transform(idPrefixRemoved).toString();
  }

  getZigbeeDeviceName(deviceId: string): string {
    let deviceName;

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
        FeatureNames.deviceName,
        deviceId
      )
      .subscribe((deviceNameFeature) => {
        deviceName = deviceNameFeature?.properties?.name?.value;
      });

    return deviceName ? deviceName : '';
  }

  getZigbeeDeviceRoomName(deviceId: string): string {
    let roomName;
    this.installationFeaturesStore.getDeviceRoom(deviceId).subscribe((roomFeature) => {
      roomName = roomFeature?.properties?.name?.value;
    });

    return roomName;
  }

  private translateFeatureName(feature: string, commandName: string): string {
    const featureName = this.FEATURES.find((f) => f === feature?.replace(/[0-9]/g, 'X'));

    if (featureName) {
      const key =
        '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED_TYPE.' +
        featureName.replace(/[.]/g, '_').toUpperCase() +
        '.COMMAND.' +
        commandName;

      return this.translateService.instant(key);
    } else {
      this.appInsights.logEvent(APP_INSIGHTS_EVENT_LOG.TRANSLATE_FEATURE, LogLevelEnum.ERROR, {
        missingFeature: feature,
        command: commandName,
      });

      return this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.EVENT_LOG.FEATURE_CHANGED_TYPE.DEFAULT_FEATURE'
      );
    }
  }

  private translateFeatureMode(featureName: string, mode: string): string {
    switch (featureName) {
      case 'ventilation.operating.modes.active':
        return (
          this.translateService.instant(this.customTranslationsService.VENTILATION_MODES[mode]) ??
          mode
        );
      case 'heating.circuits..operating.modes.active':
        //this case with two dots is checked and expected, case covered by unit tests
        return (
          this.translateService.instant(this.customTranslationsService.OPERATING_MODES[mode]) ??
          mode
        );
      case 'heating.dhw.operating.modes.active':
        return (
          this.translateService.instant(this.customTranslationsService.DHW_OPERATING_MODES[mode]) ??
          mode
        );
      default:
        return mode;
    }
  }
}
